import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, useMediaQuery, useTheme } from '@mui/material';
import { Reply, Close, Check } from '@mui/icons-material';
import { IMessage, IMessageReaction } from '../../redux/messages/types';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IUserSummary } from '../../types/global/helper';
import { AuditionMessageChat } from './AuditionMessageChat';
import { ApplicationInviteChat } from './ApplicationInviteChat';
import { VideoMessageComponent } from './VideoMessageComponent';
import { AudioMessageComponent } from './AudioMessageComponent';
import { DocumentMessageComponent } from './DocumentMessageComponent';
import { LocationMessageComponent } from './LocationMessageComponent';
import { MediaRequestComponent } from './MediaRequestComponent';
import { PictureMessageComponent } from '../ChatBubble/PictureMessageComponent';
import { MessageActions } from './MessageActions';
import DustParticles from './DustParticles';

interface ICustomReaction {
    emoji: string;
    users: string[];
}

interface MessageBubbleProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReply: (message: IMessage) => void;
    onReactionClick: (emoji: string) => void;
    formatMessageTime: (timestamp?: string) => string;
    getUserInfo: (userId: string | IUserSummary) => IUserSummary;
    formatReactionUsers: (reaction: ICustomReaction) => string;
    convertReactionsToCustomFormat: (reactions?: IMessageReaction[]) => ICustomReaction[];
    scrollToMessage: (messageId: string) => void;
    handleMessageLongPress: (e: React.MouseEvent<HTMLDivElement>, messageId: string) => void;
    user_id: string;
    isPrevSameUser: boolean;
    isNextSameUser: boolean;
    replyingTo?: IMessage;
    onDeleteMessage?: (messageId: string) => void;
    onEditMessage?: (messageId: string, newContent: string) => void;
}

// Extend the IMessage interface to include the edited property
declare module '../../redux/messages/types' {
    interface IMessage {
        edited?: boolean;
        deleted?: boolean;
    }
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    isCurrentUser,
    onReply,
    onReactionClick,
    formatMessageTime,
    getUserInfo,
    formatReactionUsers,
    convertReactionsToCustomFormat,
    scrollToMessage,
    handleMessageLongPress,
    user_id,
    isPrevSameUser,
    isNextSameUser,
    replyingTo,
    onDeleteMessage,
    onEditMessage,
}) => {
    const { themeStyle, device } = useThemeContext();
    const uiTheme = useTheme();
    const smallScreens = useMediaQuery(uiTheme.breakpoints.down(768));
    const customReactions = convertReactionsToCustomFormat(message.reactions);

    // Refs for stable anchor elements
    const messageBoxRef = useRef<HTMLDivElement>(null);
    const editTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [messageSize, setMessageSize] = useState({ width: 0, height: 0 });
    const [messagePosition, setMessagePosition] = useState({ top: 0, left: 0 });

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(message.message_content || '');

    // Add state for handling delete animation
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeletedBubble, setShowDeletedBubble] = useState(false);
    const [showDustEffect, setShowDustEffect] = useState(false);
    const previousDeletedState = useRef(message.deleted);

    // Set cursor at end when edit mode is activated
    useEffect(() => {
        if (isEditing && editTextareaRef.current) {
            const length = editContent.length;
            editTextareaRef.current.focus();
            editTextareaRef.current.setSelectionRange(length, length);
        }
    }, [isEditing, editContent]);

    // Monitor for message deletion from Redux store
    useEffect(() => {
        // Check if message was just deleted via Redux (not via local UI deletion)
        if (message.deleted && !previousDeletedState.current && !isDeleting && !showDeletedBubble) {
            // Get current message dimensions and position before starting animation
            if (messageBoxRef.current) {
                const { width, height, top, left } = messageBoxRef.current.getBoundingClientRect();
                setMessageSize({ width, height });
                setMessagePosition({ top, left });
            }

            // Start dust effect and fade out message
            setShowDustEffect(true);
            setIsDeleting(true);

            // Wait for animation to finish
            setTimeout(() => {
                // Hide dust effect and show deleted bubble
                setShowDustEffect(false);
                setIsDeleting(false);
                setShowDeletedBubble(true);
            }, 1500); // Match animation duration
        }

        // Update ref for next comparison
        previousDeletedState.current = message.deleted;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message.deleted]);

    // Handle deleted message effect
    useEffect(() => {
        if (message.deleted && !showDeletedBubble && !isDeleting) {
            setShowDeletedBubble(true);
        }
    }, [message.deleted, showDeletedBubble, isDeleting]);

    // Add effect to measure message size for particles
    useEffect(() => {
        if (messageBoxRef.current) {
            const { width, height, top, left } = messageBoxRef.current.getBoundingClientRect();
            setMessageSize({ width, height });
            setMessagePosition({ top, left });
        }
    }, [message, isEditing]);

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditContent(message.message_content || '');
    };

    const handleSaveEdit = () => {
        if (onEditMessage && editContent.trim() !== '') {
            onEditMessage(message._id, editContent.trim());
        }
        setIsEditing(false);
    };

    const handleEditMessage = () => {
        // Only allow editing for 'chat' type messages
        if (message.message_type !== 'chat') return;

        setIsEditing(true);
        setEditContent(message.message_content || '');
    };

    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEdit();
        }
    };

    const handleDeleteMessage = (messageId: string) => {
        // Get current message dimensions and position before starting animation
        if (messageBoxRef.current) {
            const { width, height, top, left } = messageBoxRef.current.getBoundingClientRect();
            setMessageSize({ width, height });
            setMessagePosition({ top, left });
        }

        // Start dust effect and fade out message
        setShowDustEffect(true);
        setIsDeleting(true);

        // Wait for animation to finish before calling the delete function
        setTimeout(() => {
            if (onDeleteMessage) {
                onDeleteMessage(messageId);
            }
            // Wait for a bit after deletion before showing the deleted bubble
            setTimeout(() => {
                setShowDustEffect(false);
                setIsDeleting(false);
                setShowDeletedBubble(true);
            }, 500);
        }, 1500); // Match animation duration
    };

    const NormalBubble = () => {
        // If the message is deleted, render the deleted message bubble
        if (message.deleted || showDeletedBubble) {
            return (
                <Box
                    sx={{
                        backgroundColor: isCurrentUser
                            ? `${theme[themeStyle].tertiary_green}80` // 50% opacity
                            : `${theme[themeStyle].deep_grey}80`,
                        borderRadius: getBubbleBorderRadius(),
                        p: 2,
                        position: 'relative',
                        transition: 'all 0.2s',
                        opacity: 0.7,
                        borderStyle: 'dashed',
                        borderWidth: '1px',
                        borderColor: theme[themeStyle].grey,
                        maxWidth: { xs: '40vw', sm: '100%' }, // Limit width on mobile
                        ...(isNextSameUser && {
                            mb: 0.2,
                        }),
                        ...(isPrevSameUser && {
                            mt: 0.2,
                        }),
                    }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].grey,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontStyle: 'italic',
                            textAlign: 'center',
                        }}>
                        Message deleted
                    </Typography>
                </Box>
            );
        }

        if (message.audition) {
            return (
                <AuditionMessageChat
                    audition={message.audition}
                    isCurrentUser={isCurrentUser}
                    message={message}
                    onReactionClick={onReactionClick}
                    onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                    formatMessageTime={formatMessageTime}
                />
            );
        }
        return (
            <Box
                ref={messageBoxRef}
                onClick={e => handleMessageLongPress(e, message._id)}
                sx={{
                    backgroundColor: isCurrentUser ? theme[themeStyle].tertiary_green : theme[themeStyle].deep_grey,
                    borderRadius: getBubbleBorderRadius(),
                    p: 2,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    maxWidth: { xs: '40vw', sm: '100%' }, // Limit width on mobile
                    // Fade out during deletion but don't animate the container itself
                    ...(isDeleting && {
                        opacity: 0,
                        transition: 'opacity 1.5s ease-out',
                    }),
                    // Add a connector line for consecutive messages
                    ...(isNextSameUser && {
                        mb: 0.2, // Smaller margin for connected messages
                    }),
                    ...(isPrevSameUser && {
                        mt: 0.2, // Smaller margin for connected messages
                    }),
                    '&:hover': {
                        opacity: isDeleting ? 0 : 0.9,
                        '& .message-actions': {
                            opacity: 1,
                        },
                    },
                }}>
                {/* Content of the message - removed dust particles since it's now at component level */}
                {isEditing ? (
                    <Box sx={{ width: '100%' }}>
                        <textarea
                            ref={editTextareaRef}
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '10px',
                                backgroundColor: 'transparent',
                                color: theme[themeStyle].tag_white,
                                border: `1px solid ${theme[themeStyle].grey}50`,
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                fontSize: '16px',
                                resize: 'vertical',
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton size="small" onClick={handleCancelEdit} sx={{ color: theme[themeStyle].grey }}>
                                <Close fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleSaveEdit}
                                sx={{ color: theme[themeStyle].highlight }}>
                                <Check fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                wordBreak: 'break-word',
                                fontSize: { xs: '12px', sm: '14px', md: '16px' },
                            }}>
                            {message.message_content}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                }}>
                                {formatMessageTime(message.createdAt)}
                                {message.edited && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                            ml: 1,
                                            fontStyle: 'italic',
                                            color: isCurrentUser
                                                ? 'rgba(255, 255, 255, 0.5)'
                                                : theme[themeStyle].grey + '80',
                                        }}>
                                        (edited)
                                    </Typography>
                                )}
                            </Typography>

                            <MessageActions
                                message={message}
                                isCurrentUser={isCurrentUser}
                                onReactionClick={emoji => onReactionClick(emoji)}
                                onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                                onEditMessage={handleEditMessage}
                            />
                        </Box>

                        {/* Message actions - visible on hover */}
                        <Box
                            className="message-actions"
                            sx={{
                                position: 'absolute',
                                top: '-20px',
                                right: isCurrentUser ? 'auto' : '10px',
                                left: isCurrentUser ? '10px' : 'auto',
                                display: 'flex',
                                bgcolor: theme[themeStyle].deep_grey,
                                borderRadius: '15px',
                                boxShadow: 2,
                                opacity: 0,
                                transition: 'opacity 0.2s',
                            }}>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation();
                                    onReply(message);
                                }}
                                sx={{ color: theme[themeStyle].highlight }}>
                                <Reply fontSize="small" />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    const renderMessageContent = () => {
        // If message is being deleted, just render the normal bubble (which will animate)
        if (isDeleting || message.deleted) {
            return <NormalBubble />;
        }

        // Otherwise proceed with normal message type rendering
        switch (message.message_type) {
            case 'audition_invite':
                return message.audition ? (
                    <AuditionMessageChat
                        audition={message.audition}
                        isCurrentUser={isCurrentUser}
                        message={message}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'application_invite':
            case 'user_invite':
                return message.role && message.audition ? (
                    <ApplicationInviteChat
                        role={message.role}
                        audition={message.audition}
                        isCurrentUser={isCurrentUser}
                        type={message.message_type}
                        message={message}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'media_request':
                return <MediaRequestComponent message={message} isCurrentUser={isCurrentUser} />;

            case 'video_message':
                return message.media ? (
                    <VideoMessageComponent
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'picture_message':
                return message.image ? (
                    <PictureMessageComponent
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'sound_message':
                return message.audio ? (
                    <AudioMessageComponent
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'document_message':
                return message.document ? (
                    <DocumentMessageComponent
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'location_message':
                return message.location && message.location.coordinates ? (
                    <LocationMessageComponent
                        message={message}
                        isCurrentUser={isCurrentUser}
                        onReactionClick={onReactionClick}
                        onDeleteMessage={messageId => handleDeleteMessage(messageId)}
                        formatMessageTime={formatMessageTime}
                    />
                ) : (
                    <NormalBubble />
                );

            case 'chat':
            default:
                // Render normal text message
                return <NormalBubble />;
        }
    };

    // Determine border radius based on message position in chain
    const getBubbleBorderRadius = () => {
        if (isCurrentUser) {
            // Right side messages (current user)
            if (isPrevSameUser && isNextSameUser) {
                // Middle message in chain
                return '4px 4px 4px 4px';
            } else if (isPrevSameUser && !isNextSameUser) {
                // Last message in chain (connected to avatar)
                // Make bottom-right corner sharp (anchored to avatar)
                return '4px 4px 0px 16px';
            } else if (isNextSameUser) {
                // First message in chain
                return '16px 16px 4px 4px';
            }
            // Single message (connected to avatar)
            // Make bottom-right corner sharp (anchored to avatar)
            return '16px 16px 0px 16px';
        } else {
            // Left side messages (other users)
            if (isPrevSameUser && isNextSameUser) {
                // Middle message in chain
                return '4px 4px 4px 4px';
            } else if (isPrevSameUser && !isNextSameUser) {
                // Last message in chain (connected to avatar)
                // Make bottom-left corner sharp (anchored to avatar)
                return '4px 4px 16px 0px';
            } else if (isNextSameUser) {
                // First message in chain
                return '16px 16px 4px 4px';
            }
            // Single message (connected to avatar)
            // Make bottom-left corner sharp (anchored to avatar)
            return '16px 16px 16px 0px';
        }
    };

    const avatarWidth = device.isMobile ? 20 : 30;

    return (
        <Box
            id={`message-${message._id}`}
            sx={{
                display: 'flex',
                justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                mb: isNextSameUser ? 0.5 : 2, // Reduce margin for consecutive messages from same user
                width: '100%',
                position: 'relative', // For absolute positioning of dust effect
                //  overflowX: 'hidden',
                // overflowY: 'hidden',
                px: { sm: 1, md: 0 },
                pr: { xs: 1, md: 0 },
            }}>
            {/* Dust particle effect - render at the component level */}
            {showDustEffect && (
                <DustParticles
                    isActive={showDustEffect}
                    isCurrentUser={isCurrentUser}
                    width={messageSize.width}
                    height={messageSize.height}
                    left={messagePosition.left}
                    top={messagePosition.top}
                />
            )}

            {/* Show avatar only for last message in a chain or single message */}
            {!isCurrentUser && !isNextSameUser && (
                <Box sx={{ mr: 1, alignSelf: 'flex-end' }}>
                    <ThspianAvatar
                        user={message.created_by}
                        sx={{ width: avatarWidth, height: avatarWidth }}
                        size={smallScreens ? 'small' : 'medium'}
                    />
                </Box>
            )}
            {/* Space holder for alignment when no avatar is shown */}
            {!isCurrentUser && isNextSameUser && <Box sx={{ width: 40, mr: smallScreens ? -1.2 : 0.2 }} />}
            <Box
                sx={{
                    maxWidth: {
                        xs: '40vw', // 40% of viewport width on mobile
                        sm: '70%',
                    },
                }}>
                {/* Reply reference */}
                {message.is_reply && replyingTo && (
                    <Box
                        onClick={() => replyingTo && scrollToMessage(replyingTo._id)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: isCurrentUser ? 1 : 0,
                            mr: isCurrentUser ? 0 : 1,
                            mb: 0.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: theme[themeStyle].deep_grey + '80',
                            cursor: 'pointer',
                            borderLeft: `3px solid ${theme[themeStyle].highlight}`,
                            maxWidth: '100%',
                        }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].highlight,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                display: 'block',
                                mb: 0.5,
                            }}>
                            Reply to{' '}
                            {typeof replyingTo.created_by === 'string'
                                ? getUserInfo(replyingTo.created_by).firstName
                                : replyingTo.created_by.firstName}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                width: '100%',
                            }}>
                            {replyingTo.message_content.length > 50
                                ? `${replyingTo.message_content.substring(0, 50)}...`
                                : replyingTo.message_content}
                        </Typography>
                    </Box>
                )}

                {/* Message content based on type */}
                {renderMessageContent()}

                {/* Reactions */}
                {customReactions.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            mt: 0.5,
                            position: 'relative',
                            zIndex: 1,
                            justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                        }}>
                        {customReactions.map((reaction, idx) => (
                            <Tooltip
                                key={`${reaction.emoji}-${idx}`}
                                title={formatReactionUsers(reaction)}
                                placement="top">
                                <Chip
                                    size="small"
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}>
                                            <span style={{ marginRight: '4px' }}>{reaction.emoji}</span>
                                            <Typography variant="caption">{reaction.users.length}</Typography>
                                        </Box>
                                    }
                                    sx={{
                                        bgcolor: theme[themeStyle].deep_grey,
                                        color: theme[themeStyle].tag_white,
                                        border: reaction.users.includes(user_id)
                                            ? `1px solid ${theme[themeStyle].highlight}`
                                            : 'none',
                                        height: 24,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        },
                                        '& .MuiChip-label': {
                                            px: 1,
                                        },
                                    }}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onReactionClick(reaction.emoji);
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </Box>
            {/* Show avatar only for last message in a chain or single message */}
            {isCurrentUser && !isNextSameUser && (
                <Box sx={{ ml: 1, alignSelf: 'flex-end' }}>
                    <ThspianAvatar
                        user={message.created_by}
                        sx={{ width: avatarWidth, height: avatarWidth, fontSize: smallScreens ? '4px' : '12px' }}
                        size={smallScreens ? 'small' : 'medium'}
                    />
                </Box>
            )}
            {/* Space holder for alignment when no avatar is shown */}
            {isCurrentUser && isNextSameUser && <Box sx={{ width: 40, ml: smallScreens ? -1.2 : 0.2 }} />}
        </Box>
    );
};

export default MessageBubble;
